import React, { useEffect, useState } from 'react';
import { elements } from '@ecosystem/newron-design-system';
import { Entity } from '@ecosystem/newron-core/types';
import { RecentMarket } from './types';
import { Market } from '../types';
import { currentProposition } from '../../utils/currentProposition';

const { Icon } = elements;

export const RECENT_MARKETS_KEY = () => `${currentProposition()}_recents`;

type RecentRowMarket = {
  product: Entity;
  countries: Entity[];
};

type RecentRow = RecentMarket & {
  displayDate: string;
  markets: RecentRowMarket[];
};

type Props = {
  markets: Market[];
  onSelect: (cells: Entity[]) => void;
};
const RecentMarkets = ({ markets, onSelect }: Props) => {
  const [recentEntries, setRecentEntries] = useState<RecentMarket[] | undefined>();
  const [products, setProducts] = useState<Record<string, Entity>>();
  const [countries, setCountries] = useState<Record<string, Entity>>();

  useEffect(() => {
    const storedEntry = localStorage.getItem(RECENT_MARKETS_KEY());
    const entries = storedEntry ? (JSON.parse(storedEntry) as RecentMarket[]) : [];
    setRecentEntries(entries);
  }, []);

  useEffect(() => {
    if (!markets) {
      return;
    }
    const products: Record<string, Entity> = {};
    const countries: Record<string, Entity> = {};
    markets.forEach(market => {
      products[market.productCode] = {
        value: market.productCode,
        label: market.productName,
      };
      countries[market.countryCode] = {
        value: market.countryCode,
        label: market.countryName,
      };
      setProducts(products);
      setCountries(countries);
    });
  }, [markets]);

  const formatter = new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  const rows: RecentRow[] = [];
  if (markets && recentEntries) {
    recentEntries.forEach(entry => {
      let allValid = true;
      const rowMarkets: RecentRowMarket[] = [];
      const productCodes = new Set<string>();
      for (let cell of entry.cells) {
        if (!markets.find(m => m.value === cell.value)) {
          allValid = false;
          break;
        }
        const parts = cell.value.split('-');
        if (!productCodes.has(parts[1])) {
          productCodes.add(parts[1]);
          const product = products[parts[1]];
          const country = countries[parts[0]];
          rowMarkets.push({
            product,
            countries: [country],
          });
        } else {
          const country = countries[parts[0]];
          const rowMarket = rowMarkets.find(rm => rm.product.value === parts[1]);
          rowMarket?.countries.push(country);
          rowMarket?.countries.sort((a, b) => a.value.localeCompare(b.value));
        }
      }
      if (allValid) {
        rowMarkets.sort((a, b) => a.product.label.localeCompare(b.product.label));
        rows.push({
          ...entry,
          displayDate: formatter.format(new Date(entry.used)),
          markets: rowMarkets,
        });
      }
    });
  }

  const handleRowClick = (row: RecentRow) => {
    onSelect(row.cells);
  };

  return (
    <div className="w-192 overflow-y-auto overscroll-contain px-4">
      <div className="text-heading text-lg py-3">Recently used markets</div>
      {rows.length === 0 && (
        <div className="flex gap-2 pt-10 text-light text-sm">
          <Icon color="info">
            <Icon.icons.Info />
          </Icon>
          Recent markets you view will be visible here so you can easily retrieve them again.
        </div>
      )}
      {rows.map((row, idx) => (
        <RowButton key={idx} row={row} onClick={() => handleRowClick(row)} />
      ))}
    </div>
  );
};

type RowProps = {
  row: RecentRow;
  onClick: () => void;
};
const RowButton = ({ row, onClick }: RowProps) => {
  const { displayDate, markets } = row;

  return (
    <button
      className="relative mb-2 w-full max-w-full px-2 py-2 flex text-left rounded text-sm hover:bg-light"
      onClick={onClick}
    >
      <div className="flex-grow">
        {markets.map(market => (
          <div key={market.product.value} className="flex gap-1 pb-1">
            <div className="flex-none font-bold">{market.product.label}</div>
            <div className="text-lighter flex-none">in</div>
            <div className="flex-grow">
              {market.countries.length === 1 && market.countries[0].value}
              {market.countries.length > 1 &&
                `${market.countries
                  .slice(0, -1)
                  .map(c => c.value)
                  .join(', ')} & ${market.countries[market.countries.length - 1].value}`}
            </div>
          </div>
        ))}
      </div>
      <div className="flex-none pl-4">
        <span className="block text-lighter font-bold">Last viewed</span>
        <span>{displayDate}</span>
      </div>
    </button>
  );
};

const updateRecentMarkets = (cells: Entity[]) => {
  const storedEntry = localStorage.getItem(RECENT_MARKETS_KEY());
  const entries = storedEntry ? (JSON.parse(storedEntry) as RecentMarket[]) : [];

  const sortedCells = [...cells].sort((a, b) => a.value.localeCompare(b.value));
  const cellStr = sortedCells.join(',');

  const matchingEntry = entries.findIndex(entry => {
    const sortedEntryCells = [...entry.cells].sort((a, b) => a.value.localeCompare(b.value));
    const checkCellStr = sortedEntryCells.join(',');
    return checkCellStr === cellStr;
  });

  if (matchingEntry !== -1) {
    entries.splice(matchingEntry, 1);
  }

  if (entries.length >= 10) {
    entries.pop();
  }
  entries.unshift({
    used: Date.now(),
    cells,
  });
  localStorage.setItem(RECENT_MARKETS_KEY(), JSON.stringify(entries));
};

export { RecentMarkets, updateRecentMarkets };
